'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import {
  cn,
  Price,
  PriceRange,
  testRideFormSchema,
  useLazyGetStoreListQuery,
  z,
} from '@segway/core'
import { StoreResult } from '@segway/core/src/graphql/generated/graphql'
import { useToggle } from 'ahooks'
import { format } from 'date-fns'
import cloneDeep from 'lodash/cloneDeep'

import { TestRideForm, TestRideFormRef } from '@/app/[locale]/[...url]/_pages/product/components'
import { useProduct, useProductData } from '@/app/[locale]/[...url]/_pages/product/ProductProvider'
import {
  ACTION_TYPE,
  type ActionType,
  Arrow,
  CloseBgIcon,
  Dialog,
  EmailIcon,
  EmptyStoreIcon,
  HomeDeliveryIcon,
  LocationIcon,
  PickIcon,
  RepairIcon,
  SafeAreaBottom,
  StoreIcon,
  StoreMap,
  TelephoneIcon,
  TestRideIcon,
  TimeIcon,
} from '@/components'
import { Link } from '@/i18n/navigation'
import { Button, Drawer, DrawerClose, DrawerContent, Skeleton } from '@/ui'

import './style.css'

interface ChooseStorePopupProps {
  actionType: ActionType
  isHomeDelivery: boolean
  toggleChooseStoreDialog: () => void
  toggleCartAwaitDialog?: () => void
  onChangeStoreSuccess?: () => void
}

/**
 * 选择店铺弹窗
 */
const ChooseStorePopup = ({
  actionType,
  isHomeDelivery,
  toggleChooseStoreDialog,
  toggleCartAwaitDialog,
  onChangeStoreSuccess,
}: ChooseStorePopupProps) => {
  const {
    selectedBundle,
    quantity,
    responsive,
    getStoreAddress,
    setSelectedStore: updateSelectedStore,
    handleChangeStoreOnCart,
    addToCart,
    createTestRide,
  } = useProduct()
  const product = useProductData()

  const [storeList, setStoreList] = useState<StoreResult[]>([])
  const [showStoreMapDialog, { toggle: toggleStoreMapDialog }] = useToggle(false)
  const [isLoading, setIsLoading] = useState(false)

  const [selectedStore, setSelectedStore] = useState<StoreResult | null>(null)
  const [selectedStoreFromMap, setSelectedStoreFromMap] = useState<StoreResult | null>(null)

  const [getStoreList] = useLazyGetStoreListQuery()

  const testRideFormRef = useRef<TestRideFormRef>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  /**
   * 是否 pickup
   */
  const isPickup = useMemo(() => actionType === ACTION_TYPE.pickup, [actionType])

  /**
   * 是否 test ride
   */
  const isTestRide = useMemo(() => actionType === ACTION_TYPE.testRide, [actionType])

  /**
   * 是否 change store
   */
  const isChangeStore = useMemo(() => actionType === ACTION_TYPE.changeStore, [actionType])

  /**
   *  展示的店铺
   */
  const finalStore = useMemo(() => {
    if (selectedStoreFromMap) {
      return [selectedStoreFromMap]
    }

    return storeList
  }, [storeList, selectedStoreFromMap])

  /**
   * 选中的店铺
   */
  const finalSelectedStore = useMemo(() => {
    if (selectedStoreFromMap) {
      return selectedStoreFromMap
    }

    return selectedStore
  }, [selectedStore, selectedStoreFromMap])

  /**
   * 门店地图回调
   */
  const handleSelectFromMap = async (store: StoreResult, type = false) => {
    if (isChangeStore && store?.id) {
      const result = await handleChangeStoreOnCart(store.id, type)
      if (result && onChangeStoreSuccess) {
        onChangeStoreSuccess()
      }
      toggleStoreMapDialog()
      toggleChooseStoreDialog()
    } else {
      setSelectedStoreFromMap(store)
      updateSelectedStore(store)
      toggleStoreMapDialog()
    }
  }

  /**
   * 点击加购
   */
  const handleAddToCart = async () => {
    const result = await addToCart(isHomeDelivery)
    toggleChooseStoreDialog()
    if (result && toggleCartAwaitDialog) {
      toggleCartAwaitDialog()
    }
  }

  /**
   * 点击切换门店
   */
  const handleChangeStore = async (type = false) => {
    const result = await handleChangeStoreOnCart(finalSelectedStore?.id as number, type)
    if (result && onChangeStoreSuccess) {
      onChangeStoreSuccess()
    }
    toggleChooseStoreDialog()
  }

  /**
   *  提交 test ride
   */
  const submitTestRide = async (values: z.infer<typeof testRideFormSchema>) => {
    const input = {
      store_id: selectedStoreFromMap ? selectedStoreFromMap.store_id : selectedStore?.store_id,
      product_sku: product?.sku,
      firstname: values.firstName,
      lastname: values.lastName,
      customer_email: values.email,
      customer_phone: values.phone,
      datetime: `${format(values.date, 'yyyy-MM-dd')} ${values.time}`,
    }

    await createTestRide({
      input,
    })
    toggleChooseStoreDialog()
  }

  /**
   * 点击 test ride
   */
  const handleTestRide = async () => {
    if (testRideFormRef.current) {
      const form = testRideFormRef.current.form
      await form.trigger()
      if (!!Object.keys(form.formState.errors)?.length) {
        return
      }
      form.handleSubmit(submitTestRide)()
    }
  }

  /**
   * 点击选择门店
   */
  const handleClickStore = (store: StoreResult) => {
    if (selectedStoreFromMap) return
    setSelectedStore(store)
    updateSelectedStore(store)
  }

  /**
   * 获取门店列表
   */
  const fetchStoreList = useCallback(
    (latAndLng: { lat?: number; lng?: number } = {}) => {
      getStoreList(
        latAndLng.lat && latAndLng.lng
          ? {
              latitude: latAndLng.lat.toString(),
              longitude: latAndLng.lng.toString(),
              address: '',
            }
          : { address: '' },
      )
        .unwrap()
        .then((res) => {
          if (res?.getStoreList) {
            let resultFiltered = cloneDeep(res?.getStoreList) || []

            // 对源数据先进行条件过滤
            if (actionType !== ACTION_TYPE.testRide && resultFiltered.length > 0) {
              resultFiltered.forEach((store) => {
                if (store?.distance_value) {
                  const distance = +store.distance_value
                  if (distance < 50) {
                    store.service = '0'
                  }
                  if (distance > 100) {
                    store.pickup = '0'
                  }
                }
              })
              resultFiltered = resultFiltered.filter((store) => {
                return !(store?.service === '0' && store?.pickup === '0')
              })
            }

            if (isPickup) {
              resultFiltered = resultFiltered.filter((store) =>
                isHomeDelivery ? store?.service === '1' : store?.pickup === '1',
              )
            }

            if (isTestRide) {
              resultFiltered = resultFiltered.filter((store) => store?.test_drive === '1')
            }

            setStoreList(resultFiltered as StoreResult[])
          }
        })
        .catch((err) => {
          console.log(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [getStoreList, isPickup, isTestRide, actionType, isHomeDelivery],
  )

  /**
   * 获取用户当前位置
   */
  useEffect(() => {
    setIsLoading(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        fetchStoreList({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
      },
      () => {
        fetchStoreList()
      },
      {
        // 可选配置项
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
    )
  }, [fetchStoreList])

  useEffect(() => {
    if (selectedStoreFromMap) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [selectedStoreFromMap])

  return (
    <div>
      {/* {responsive && responsive.xl && <div className="my-9 h-[1px] bg-gray-2"></div>} */}

      <div
        className={cn(
          'store-list-container max-h-[550px] overflow-y-scroll px-5 pb-[76px] xl:max-h-[620px] xl:px-0 xl:pb-[80px]',
          finalSelectedStore ? 'pb-[52px] xl:pb-[98px]' : '',
          responsive && responsive.xl ? 'mt-9' : 'mt-2',
        )}>
        <div className="mb-3 flex gap-4 xl:mb-7 xl:gap-[20px]">
          <div
            className={`flex ${responsive && responsive.xl ? 'h-[160px] w-[160px]' : 'h-[126px] w-[126px]'} overflow-hidden rounded-md`}>
            <Image
              src={product?.small_image?.url || ''}
              alt="product"
              width={responsive && responsive.xl ? 160 : 126}
              height={responsive && responsive.xl ? 160 : 126}
              style={{ objectFit: 'contain' }}
              className="flex-none bg-gray-1"
            />
          </div>

          <div className="flex-1 xl:py-2">
            <p className="line-clamp-2 font-miSansSemiBold520 text-lg text-gray-8 xl:text-2xl">
              {product?.name}
            </p>
            <div className="mt-5">
              {product?.price_range && (
                <PriceRange
                  priceRange={product.price_range}
                  priceProps={{
                    textStyle: 'xl:text-[18px] text-lg',
                    currencyStyle: 'xl:text-[18px] text-lg',
                    fractionStyle: 'xl:text-[18px] text-lg',
                  }}
                  originalPriceProps={{
                    textStyle: 'xl:text-[18px] text-lg',
                    currencyStyle: 'xl:text-[18px] text-lg',
                    fractionStyle: 'xl:text-[18px] text-lg',
                  }}
                />
              )}
            </div>
            <div className="mt-5 flex items-center">
              <span className="font-miSansRegular330 text-lg text-black">×{quantity}</span>
            </div>
          </div>
        </div>
        {!isTestRide &&
          !!selectedBundle?.length &&
          selectedBundle.map((item) => (
            <div key={item.sku} className="mb-3 flex gap-3 xl:mb-7">
              <div className="flex h-[126px] w-[126px] overflow-hidden rounded-md">
                <Image
                  src={item.small_image}
                  alt="product"
                  width={126}
                  height={126}
                  style={{ objectFit: 'contain' }}
                  className="bg-gray-1"
                />
              </div>

              <div className="flex-1">
                <p className="line-clamp-2 font-miSansSemiBold520 text-lg text-gray-8 xl:text-2xl">
                  {item.name}
                </p>
                <div className="mt-[18px] xl:mt-3">
                  <Price
                    price={{ value: item.price || 0 }}
                    textStyle="xl:text-[20px] text-lg"
                    currencyStyle="xl:text-[20px] text-lg"
                    fractionStyle="xl:text-[20px] text-lg"
                  />
                </div>
                <div className="mt-[18px] flex items-center xl:mt-3">
                  <span className="font-miSansRegular330 text-[16px] text-black">×{quantity}</span>
                </div>
              </div>
            </div>
          ))}

        {isTestRide && <TestRideForm ref={testRideFormRef} />}

        <div className="mt-9 flex items-center justify-between">
          {responsive && responsive.xl && (
            <h3 className="font-miSansDemiBold450 text-2xl text-gray-8">
              {selectedStoreFromMap
                ? isTestRide
                  ? 'Test ride store'
                  : isHomeDelivery
                    ? 'home delivery from a dealer'
                    : 'Pickup in store'
                : 'Nearby Store'}
            </h3>
          )}

          {responsive && !responsive.xl && (
            <h3 className="font-miSansDemiBold450 text-lg text-gray-8">Nearby Store</h3>
          )}

          <div onClick={toggleStoreMapDialog} className="flex cursor-pointer items-center gap-1">
            <span className="font-miSansMedium380 text-base text-gray-8 xl:text-lg">
              {selectedStoreFromMap ? 'Change Store' : 'Find on map'}
            </span>
            {responsive && responsive.xl && <Arrow width={20} height={20} color="#6E6E73" />}
            {responsive && !responsive.xl && <Arrow width={16} height={16} color="#6E6E73" />}
          </div>
        </div>

        {!!finalStore?.length && (
          <div className="mt-6 space-y-4">
            {finalStore.map((store, index) => (
              <div
                key={store.id}
                onClick={() => handleClickStore(store)}
                className={cn(
                  'cursor-pointer rounded-md border border-gray-2 px-4 py-4 xl:py-[20px]',
                  selectedStore?.id === store.id
                    ? 'border-[2px] border-primary px-[15px] py-[15px] xl:py-[19px]'
                    : '',
                  index === finalStore.length - 1 && responsive && !responsive.xl
                    ? finalStore.length === 1 || selectedStore
                      ? '!mb-[120px]'
                      : '!mb-[94px]'
                    : '',
                )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {responsive && responsive.xl && <StoreIcon />}
                    <p className="font-miSansDemiBold450 text-base text-gray-8 xl:text-xl">
                      {store.store_name}
                    </p>
                  </div>
                  {store.distance_text && (
                    <div className="flex items-center gap-1">
                      <LocationIcon />
                      <p className="font-miSansRegular330 text-base text-gray-4">
                        {store.distance_text}
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-3 flex items-center justify-between xl:mt-2">
                  {responsive && responsive.xl && (
                    <p className="font-miSansRegular330 text-base text-gray-6">
                      {getStoreAddress(store)}
                    </p>
                  )}

                  <div className="flex items-center gap-1">
                    <Link href={`tel:${store.telephone}`}>
                      <div className="flex items-center gap-1">
                        <TelephoneIcon />
                        <span className="ml-[7px] mr-3 font-miSansRegular330 text-base leading-[1.4] text-[#007AFF] xl:text-gray-6">
                          {store.telephone}
                        </span>
                      </div>
                    </Link>

                    {responsive && responsive.xl && (
                      <Link href={`mailto:${store.store_email}`}>
                        <EmailIcon />
                      </Link>
                    )}
                  </div>
                </div>

                {responsive && responsive.xl && (
                  <>
                    <div className="my-3 h-[1px] bg-gray-1"></div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex gap-2">
                        {!!store.store_time_label?.length && <TimeIcon />}
                        <div className="grid min-w-[340px] grid-cols-2 gap-x-6 gap-y-2">
                          {store.store_time_label?.map((item, index) => (
                            <p
                              key={index}
                              className="font-miSansRegular330 text-[14px] text-gray-5">
                              {item}
                            </p>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {store.test_drive === '1' && (
                          <div
                            title="Test Ride"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-1">
                            <TestRideIcon />
                          </div>
                        )}
                        {store.repair === '1' && (
                          <div
                            title="Repair"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-1">
                            <RepairIcon />
                          </div>
                        )}
                        {store.service === '1' && (
                          <div
                            title="Home Delivery"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-1">
                            <HomeDeliveryIcon />
                          </div>
                        )}
                        {store.pickup === '1' && (
                          <div
                            title="Pickup In Store"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-1">
                            <PickIcon />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}

        {!isLoading && finalStore?.length === 0 && (
          <div className="my-[87px] flex flex-col items-center justify-center">
            <EmptyStoreIcon />
            <p className="mt-3 font-miSansRegular330 text-base text-gray-4">
              No search results for now.
            </p>
          </div>
        )}

        {isLoading && (
          <div className="mt-5">
            {new Array(3).fill(0).map((_, index) => (
              <Skeleton
                key={index}
                className="mb-4 h-[145px] w-full rounded-[16px] bg-gray-0"></Skeleton>
            ))}
          </div>
        )}
      </div>

      <div className="pt-4 xl:pb-2 xl:pt-0">
        <div className="absolute bottom-0 left-0 right-0 flex gap-7 rounded-md bg-white px-5 py-4 xl:pb-9 xl:pt-0">
          {isPickup && (
            <Button
              disabled={!finalSelectedStore}
              onClick={handleAddToCart}
              className={cn(
                'h-[44px] w-full font-miSansMedium380 text-lg xl:mt-4 xl:h-[64px] xl:font-miSansDemiBold450 xl:text-2xl',
              )}>
              Add To Cart
            </Button>
          )}
          {isChangeStore && finalSelectedStore?.pickup === '1' && (
            <Button
              onClick={() => handleChangeStore()}
              className="h-[44px] w-full font-miSansMedium380 text-lg xl:mt-4 xl:h-[64px] xl:font-miSansDemiBold450 xl:text-2xl">
              Pickup in store
            </Button>
          )}
          {isChangeStore && finalSelectedStore?.service === '1' && (
            <Button
              onClick={() => handleChangeStore(true)}
              className="h-[44px] w-full font-miSansMedium380 text-lg xl:mt-4 xl:h-[64px] xl:font-miSansDemiBold450 xl:text-2xl">
              Home Delivery
            </Button>
          )}
          {isTestRide && (
            <Button
              disabled={!finalSelectedStore}
              onClick={handleTestRide}
              className="h-[44px] w-full font-miSansMedium380 text-lg xl:mt-4 xl:h-[64px] xl:font-miSansDemiBold450 xl:text-2xl">
              Book Now
            </Button>
          )}
        </div>
        {responsive && !responsive.xl && <SafeAreaBottom />}
      </div>

      {/* Store Map Dialog */}
      {responsive && responsive.xl && (
        <Dialog
          open={showStoreMapDialog}
          onOpenChange={toggleStoreMapDialog}
          containerStyle="w-[1132px] p-0">
          <StoreMap
            actionType={actionType}
            isHomeDelivery={isHomeDelivery}
            onSelectStore={handleSelectFromMap}
          />
        </Dialog>
      )}
      {responsive && !responsive.xl && (
        <Drawer
          repositionInputs={false}
          open={showStoreMapDialog}
          onOpenChange={toggleStoreMapDialog}>
          <DrawerContent className="rounded-none">
            <DrawerClose className="absolute right-3 top-3 z-10">
              <CloseBgIcon />
            </DrawerClose>
            <StoreMap
              actionType={actionType}
              isHomeDelivery={isHomeDelivery}
              onSelectStore={handleSelectFromMap}
            />
          </DrawerContent>
        </Drawer>
      )}
    </div>
  )
}

export default ChooseStorePopup
